import I18n from 'i18n-js';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import every from 'lodash/every';
import find from 'lodash/find';
import max from 'lodash/max';
import minBy from 'lodash/minBy';
import { FIELD_NAME } from 'views/v2/home/constants';
import { generateLocalisedItemOptions } from 'helper/order';
import { QUOTE_OPTIMIZED } from 'constants/booking-constants';
import {
  VERIFY_ADDRESS_STATUS,
  DEFAULT_ITEM_WEIGHT,
  JOB_TYPE,
  FINANCIAL_STATUS,
} from './consts';

export const makeMap = (objects, key = 'ID') => {
  const map = {};
  if (!objects) {
    return map;
  }

  objects.forEach(object => {
    if (object) {
      map[object[key]] = object;
    }
  });
  return map;
};

export const getAddress = (fulfillmentOrder, options = {}) => {
  if (!fulfillmentOrder || !fulfillmentOrder.shippingAddress) {
    return '';
  }
  const {
    withAddress2 = false,
    withCity = false,
    withCountryCity = false,
    withZip = false,
  } = options;
  const { shippingAddress = {} } = fulfillmentOrder;
  return [
    shippingAddress.address1,
    withAddress2 && shippingAddress.address2,
    (withCity || withCountryCity) && shippingAddress.city,
    withCountryCity && shippingAddress.country,
    withZip && shippingAddress.zip,
  ]
    .filter(p => !!p)
    .join(', ');
};

export const findBestMatchResult = (results, latitude, longitude) => {
  if (latitude && longitude) {
    const sorted = sortBy(results, r => get(r, 'distance_between', 0));
    return sorted[0];
  }
  return results[0];
  // const sorted = sortBy(results, r => -get(r, 'score', 0))
  // return sorted[0]
};

export const convertPOIResultToAddressEntity = (
  shopifyShippingAddress,
  result,
) => ({
  address: get(result, ['address', 'combined_address']),
  cityID: get(result, ['address', 'cityId']),
  latlng: get(result, 'latlng'),
  countryID: get(result, ['address', 'country_id']),
  instruction: get(shopifyShippingAddress, 'address2', ''),
  keywords: get(result, ['address', 'name']),
  poiID: get(result, 'id'),
});

export const getFulfillmentOrdersFromOrders = orders => {
  const fulfillmentOrders = [];
  if (orders) {
    orders.forEach(order => {
      order.fulfillmentOrders &&
        order.fulfillmentOrders.forEach(fulfillmentOrder => {
          if (fulfillmentOrder.status === 'open') {
            fulfillmentOrders.push({
              orderName: order.name,
              orderID: order.ID,
              createdAt: order.createdAt,
              updatedAt: order.updatedAt,
              shippingType: order.shippingType,
              financialStatus: order.financialStatus,
              note: order.note,
              ...fulfillmentOrder,
            });
          }
        });
    });
  }
  return fulfillmentOrders;
};

export const getFulfillmentOrderWeightInKg = fulfillmentOrder =>
  get(fulfillmentOrder, 'lineItems', []).reduce(
    (weight, item) =>
      weight +
      get(item, 'fulfillableQuantity', 1) *
        get(item, 'grams', DEFAULT_ITEM_WEIGHT),
    0,
  ) / 1000;

export const multipleAssignedLocationIDs = fulfillmentOrders => {
  const locationIDs = uniq(fulfillmentOrders.map(o => o.assignedLocationID));
  return locationIDs && locationIDs.length > 1;
};

export const allAddressReviewed = (fulfillmentOrders, addressEntityMap) =>
  every(
    fulfillmentOrders,
    o =>
      get(addressEntityMap, [o.ID, 'status']) ===
      VERIFY_ADDRESS_STATUS.verified,
  );

export const maxMSDStopsAmongAllServiceTypes = serviceTypes =>
  max(serviceTypes.map(st => get(st, 'maxMSDStops', 1)));

export const filterMSDServiceTypesByStops = (serviceTypes, MSDStops = 2) => {
  if (!serviceTypes) {
    return serviceTypes;
  }
  return serviceTypes.filter(st => get(st, 'maxMSDStops', 1) >= MSDStops);
};

export const maxWeightAmongAllServiceTypes = serviceTypes =>
  max(serviceTypes.map(st => get(st, 'maxWeight', 1)));

export const filterMSDServiceTypesByWeight = (
  serviceTypes,
  fulfillmentOrders,
) => {
  if (!serviceTypes) {
    return serviceTypes;
  }

  const totalWeight = fulfillmentOrders.reduce(
    (totalWeight, fulfillmentOrder) =>
      totalWeight + getFulfillmentOrderWeightInKg(fulfillmentOrder),
    0,
  );

  return {
    totalWeight,
    serviceTypes: serviceTypes.filter(
      st => get(st, 'maxWeight', 1) >= totalWeight,
    ),
  };
};

export const convertServiceType = serviceType => ({
  serviceID: serviceType.ID,
  expressServiceID: serviceType.expressServiceID,
  serviceTypeName: serviceType.name,
});

export const getCashOnDeliveryInfo = fulfillmentOrder =>
  fulfillmentOrder.financialStatus === FINANCIAL_STATUS.PENDING
    ? {
        enable: true,
        amount:
          get(fulfillmentOrder, 'subtotalPrice', 0) +
          get(fulfillmentOrder, 'splitShippingFee', 0),
      }
    : {
        enable: false,
      };

export const convertFulfillmentOrderDropOffDetail = (
  fulfillmentOrder,
  addressEntity,
) => ({
  ID: fulfillmentOrder.ID,
  orderID: fulfillmentOrder.orderID,
  dropOff: {
    contact: {
      name: get(fulfillmentOrder, ['shippingAddress', 'name'], ''),
      phone: get(fulfillmentOrder, ['shippingAddress', 'phone'], ''),
      instruction: get(addressEntity, 'instruction', ''),
    },
    jobType: JOB_TYPE.DROP_OFF,
    location: {
      address: addressEntity.address,
      keywords: addressEntity.keywords,
      coordinates: addressEntity.latlng,
      poiID: addressEntity.poiID,
    },
    cashOnDelivery: getCashOnDeliveryInfo(fulfillmentOrder),
    itemInfo: {
      // TODO: add category info
      weight: getFulfillmentOrderWeightInKg(fulfillmentOrder),
    },
  },
});

const createDeliveriesExtraInfo = () => ({
  // TODO: @bill
  quoteSignature: '',
  paymentMethod: {
    id: '',
    name: '',
    type: '',
    paidBy: '',
  },
  noteToDriver: '',
  countryCode: '',
  cityCode: '',
  paidBy: '',
  referenceCode: '',
});

const convertFulfillmentOrdersToMSDDelivery = (
  shop,
  fulfillmentOrders,
  addressEntityMap,
) => {
  // MSD: same pickUpLocation for all fulfillmentOrders
  const pickUpLocation = shop.pickUpAddresses.find(
    adr => adr.shopifyLocationID === fulfillmentOrders[0].assignedLocationID,
  );
  return {
    pickUp: pickUpLocation,
    fulfillmentOrders: fulfillmentOrders.map(fulfillmentOrder =>
      convertFulfillmentOrderDropOffDetail(
        fulfillmentOrder,
        addressEntityMap[fulfillmentOrder.ID],
      ),
    ),
    ...createDeliveriesExtraInfo(),
  };
};

export const generateShopifyMSDQuoteRequest = (
  shop,
  fulfillmentOrders,
  serviceTypes,
  addressEntityMap,
) => ({
  quoteRequestID: 'test-quote-request-msd',
  shop: shop.shop,
  deliveries: [
    convertFulfillmentOrdersToMSDDelivery(
      shop,
      fulfillmentOrders,
      addressEntityMap,
    ),
  ],
  serviceTypes: serviceTypes.map(convertServiceType),
});

const convertFulfillmentOrderToSingleDelivery = (
  shop,
  fulfillmentOrder,
  addressEntity,
) => {
  const pickUpLocation = shop.pickUpAddresses.find(
    adr => adr.shopifyLocationID === fulfillmentOrder.assignedLocationID,
  );
  return {
    pickUp: pickUpLocation,
    fulfillmentOrders: [
      convertFulfillmentOrderDropOffDetail(fulfillmentOrder, addressEntity),
    ],
    ...createDeliveriesExtraInfo(),
  };
};

export const generateShopifySingleBookingQuotesRequest = (
  shop,
  fulfillmentOrders,
  serviceTypes,
  addressEntityMap,
) => ({
  quoteRequestID: 'quote-request-single',
  shop: shop.shop,
  deliveries: fulfillmentOrders.map(fulfillmentOrder =>
    convertFulfillmentOrderToSingleDelivery(
      shop,
      fulfillmentOrder,
      addressEntityMap[fulfillmentOrder.ID],
    ),
  ),
  serviceTypes: serviceTypes.map(convertServiceType),
});

export const getCheapestServiceTypeWithQuote = serviceTypesWithQuote =>
  minBy(
    serviceTypesWithQuote.filter(q => !q.errors),
    serviceTypeWithQuote => serviceTypeWithQuote.quote.amount,
  );

export const checkPickUpAddresses = (shop, fulfillmentOrders) => {
  if (!fulfillmentOrders || fulfillmentOrders.length === 0) {
    return null;
  }

  const pickUpAddressesToCheck = {};
  fulfillmentOrders.forEach(fulfillmentOrder => {
    if (pickUpAddressesToCheck[fulfillmentOrder.assignedLocationID]) {
      return;
    }

    pickUpAddressesToCheck[fulfillmentOrder.assignedLocationID] = find(
      shop.pickUpAddresses,
      adr => adr.shopifyLocationID === fulfillmentOrder.assignedLocationID,
    );
  });

  const unverifiedAddressNames = Object.values(pickUpAddressesToCheck)
    .filter(adr => !adr.verified)
    .map(adr => get(adr, 'shopifyLocation.name'));

  if (unverifiedAddressNames.length === 0) {
    return null;
  }

  return I18n.t('shopify.orders.validation.addressUnverified', {
    addresses: unverifiedAddressNames.join(', '),
  });
};

export const processError = callback => err => {
  switch (typeof err) {
    case 'string':
      callback(err);
      return;
    case 'object':
      if (err.json) {
        err
          .json()
          .then(errJson => callback(errJson && errJson.message))
          .catch(e => callback(`Unknown error: ${e}`));
      } else {
        callback(`Unknown error: ${err}`);
      }
      return;
    default:
      callback(`Unknown error: ${err.toString()}`);
      return;
  }
};

export const toBookBase = ({
  order,
  cheapestServiceTypeWithQuote,
  serviceTypesWithQuote,
  shop,
  userInfo,
}) => {
  const pickUpAddress = shop.pickUpAddresses.find(
    adr => adr.shopifyLocationID === order.assignedLocationID,
  );
  const defaultProductCategoryID = get(shop, 'productCategoryID');
  let defaultItemType;
  if (defaultProductCategoryID) {
    defaultItemType = generateLocalisedItemOptions({ userInfo })
      .map(({ id, iString }) => ({
        value: `${id}`,
        title: iString,
      }))
      .find(itemType => itemType.value === `${defaultProductCategoryID}`);
  }
  return {
    serviceTypes: serviceTypesWithQuote.map(st => st.serviceType),
    senderLocation: {
      id: pickUpAddress.grabPoi.poiID,
      latlng: pickUpAddress.grabPoi.coordinates,
      address: {
        name: pickUpAddress.grabPoi.keywords,
        combined_address: pickUpAddress.grabPoi.address,
      },
    },
    'name-sender': {
      name: 'name-sender',
      value: get(pickUpAddress, 'metaData.contact.name', undefined),
    },
    'phone-sender': {
      name: 'phone-sender',
      value: get(pickUpAddress, 'metaData.contact.phone', undefined),
    },
    'note-sender': {
      name: 'note-sender',
      value: get(pickUpAddress, 'metaData.locationNotes', undefined),
    },
    [FIELD_NAME.SERVICE_TYPE]: {
      name: FIELD_NAME.SERVICE_TYPE,
      value: cheapestServiceTypeWithQuote.serviceType.ID.toString(),
    },
    quote: {
      deliveries: [{ quotes: serviceTypesWithQuote.map(st => st.quote) }],
      forRender: cheapestServiceTypeWithQuote.quote,
    },
    shop: shop.shop,
    pickUpAddress,
    [FIELD_NAME.PAYMENT_METHOD]: {
      name: FIELD_NAME.PAYMENT_METHOD,
    },
    [FIELD_NAME.ITEM_TYPE]: {
      name: FIELD_NAME.ITEM_TYPE,
      value: get(defaultItemType, 'value', undefined),
    },
  };
};

export const toBookMSD = ({
  selectedFulfillmentOrders,
  cheapestServiceTypeWithQuote,
  serviceTypesWithQuote,
  shop,
  userInfo,
  singleBookingsTotalQuote,
  isRouteOptimized,
}) => {
  const recipientLocations = selectedFulfillmentOrders.map(
    ({ addressEntity }) => ({
      latlng: addressEntity.latlng,
      id: addressEntity.poiID,
      address: {
        name: addressEntity.keywords,
        country_id: addressEntity.countryID,
        cityId: addressEntity.cityID,
        combined_address: addressEntity.address,
      },
    }),
  );
  const recipientMetadata = selectedFulfillmentOrders.reduce(
    (acc, { shippingAddress }, i) =>
      Object.assign({}, acc, {
        [`name-recipient-${i}`]: {
          value: get(shippingAddress, 'name'),
        },
        [`phone-recipient-${i}`]: {
          value: get(shippingAddress, 'phone'),
        },
      }),
    {},
  );
  return {
    ...toBookBase({
      order: selectedFulfillmentOrders[0],
      cheapestServiceTypeWithQuote,
      serviceTypesWithQuote,
      shop,
      userInfo,
    }),
    recipientLocations,
    ...recipientMetadata,
    [FIELD_NAME.ITEM_WEIGHT]: {
      name: FIELD_NAME.ITEM_WEIGHT,
      value: selectedFulfillmentOrders.reduce(
        (acc, order) => (acc += getFulfillmentOrderWeightInKg(order)),
        0,
      ),
    },
    fulfillmentOrders: selectedFulfillmentOrders,
    addressEntities: selectedFulfillmentOrders.map(
      ({ addressEntity }) => addressEntity,
    ),
    singleBookingsTotalQuote,
    optimizeQuotesStatus: {
      addressFieldsNeedReorder: false,
      status: isRouteOptimized ? QUOTE_OPTIMIZED : null,
      previousQuoteForRender: null,
    },
  };
};

export const toBookMultiple = ({
  selectedFulfillmentOrders,
  cheapestServiceTypeWithQuote,
  serviceTypesWithQuoteMap,
  shop,
  userInfo,
  msdInfo,
}) =>
  selectedFulfillmentOrders.map((order, i) => {
    const { addressEntity, lineItems, shippingAddress, ID } = order;
    return {
      ...toBookBase({
        order,
        cheapestServiceTypeWithQuote: cheapestServiceTypeWithQuote[ID],
        serviceTypesWithQuote: serviceTypesWithQuoteMap[ID],
        shop,
        userInfo,
      }),
      recipientLocations: [
        {
          latlng: addressEntity.latlng,
          id: addressEntity.poiID,
          address: {
            name: addressEntity.keywords,
            country_id: addressEntity.countryID,
            cityId: addressEntity.cityID,
            combined_address: addressEntity.address,
          },
        },
      ],
      'name-recipient-0': {
        value: shippingAddress.name,
      },
      'phone-recipient-0': {
        value: shippingAddress.phone,
      },
      [FIELD_NAME.ITEM_WEIGHT]: {
        name: FIELD_NAME.ITEM_WEIGHT,
        value: getFulfillmentOrderWeightInKg(order),
      },
      lineItems,
      fulfillmentOrder: order,
      addressEntity,
      ...(i == 0 && { msdInfo }),
    };
  });

export const getUniqueOrderIDs = fulfillmentOrders =>
  uniq(fulfillmentOrders.map(o => o.orderID));

export const findPickUpPOIID = (shop, fulfillmentOrder) =>
  get(
    find(
      shop.pickUpAddresses,
      adr => adr.shopifyLocationID === fulfillmentOrder.assignedLocationID,
    ),
    'poiID',
  );

export const findDropOffPOIID = (addressEntityMap, fulfillmentOrder) =>
  get(addressEntityMap, [fulfillmentOrder.ID, 'poiID']);

export const hasCustomerPhone = fulfillmentOrder =>
  !!get(fulfillmentOrder, ['shippingAddress', 'phone']);
